import React, { useState, useEffect } from 'react';
import { Table, Form, Input, Button, Space, Card, Col, message, Tooltip, Popconfirm, Tag } from 'antd';
import { PlusOutlined, EditOutlined, TeamOutlined, DeleteOutlined, CheckCircleOutlined } from '@ant-design/icons';
import FilterPanel from '../../components/common/FilterPanel';
import { useTranslation } from 'react-i18next';
import { useTableScrollHeight } from '@/hooks/useTableScrollHeight';
import * as personnelService from '../../services/personnel/personnelService';
import LevelRuleDrawer from './LevelRuleDrawer';
import BindPersonnelDrawer from './BindPersonnelDrawer';

/**
 * Personnel Mgt - Main page
 * Manage personnel levels, merge order strategy, and bind personnel
 */
const PersonnelManagement = () => {
  const [form] = Form.useForm();
  const { t } = useTranslation();
  const { tableWrapRef, scrollY } = useTableScrollHeight();
  const [loading, setLoading] = useState(false);
  const [dataSource, setDataSource] = useState([]);
  const [ruleDrawerVisible, setRuleDrawerVisible] = useState(false);
  const [bindDrawerVisible, setBindDrawerVisible] = useState(false);
  const [currentLevel, setCurrentLevel] = useState(null);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
    showSizeChanger: true,
    showQuickJumper: true,
    showTotal: (total, range) => `${t('common.operation')} ${range[0]}-${range[1]} / ${t('common.total')} ${total}`,
  });

  const columns = [
    {
      title: t('personnel.levelName'),
      dataIndex: 'levelName',
      key: 'levelName',
      width: 160,
    },
    {
      title: t('personnel.maxOrdersPerTrip'),
      dataIndex: 'maxOrdersPerTrip',
      key: 'maxOrdersPerTrip',
      width: 180,
      render: (val) => `${val} ${t('personnel.ordersPerTrip')}`,
    },
    {
      title: t('personnel.executableOrderLevels'),
      key: 'executableOrderLevels',
      width: 320,
      render: (_, record) => {
        const getExecutableOrderLevels = (levelName) => {
          switch (levelName) {
            case 'L1':
            case 'L1-Trainee':
              return ['L1'];
            case 'L2':
            case 'L2-Skilled':
              return ['L1', 'L2'];
            case 'L3':
            case 'L3-Expert':
              return ['L1', 'L2', 'L3'];
            default:
              return [];
          }
        };
        
        const levels = getExecutableOrderLevels(record.levelName);
        return levels.join(', ');
      },
    },
    {
      title: t('personnel.boundCount'),
      dataIndex: 'boundCount',
      key: 'boundCount',
      width: 120,
      render: (val) => `${val} ${t('personnel.people')}`,
    },
    {
      title: t('personnel.wetherDefault'),
      dataIndex: 'wetherDefault',
      key: 'wetherDefault',
      width: 120,
      render: (val) => (
        val ? <Tag color="green">{t('common.yes')}</Tag> : <Tag>{t('common.no')}</Tag>
      ),
    },
    {
      title: t('personnel.actions'),
      key: 'actions',
      fixed: 'right',
      width: 120,
      render: (_, record) => (
        <Space size="small">
          <Tooltip title={t('personnel.editRules')}>
            <Button
              type="link"
              size="small"
              icon={<EditOutlined />}
              onClick={() => handleEditRules(record)}
            />
          </Tooltip>
          <Tooltip title={t('personnel.bindPersonnel')}>
            <Button
              type="link"
              size="small"
              icon={<TeamOutlined />}
              onClick={() => handleBindPersonnel(record)}
            />
          </Tooltip>
          <Tooltip title={t('common.delete')}>
            <Popconfirm
              title={t('personnel.deleteConfirm')}
              onConfirm={() => handleDelete(record.id)}
              okText={t('common.confirm')}
              cancelText={t('common.cancel')}
            >
              <Button type="link" size="small" danger icon={<DeleteOutlined />} />
            </Popconfirm>
          </Tooltip>
        </Space>
      ),
    },
  ];

  const fetchData = async (params = {}) => {
    setLoading(true);
    try {
      const searchParams = {
        ...form.getFieldsValue(),
        currentPage: pagination.current,
        pageSize: pagination.pageSize,
        ...params,
      };
      const response = await personnelService.pagePersonnelLevels(searchParams);
      if (response.success) {
        const data = response.data || {};
        setDataSource(data.tableData || []);
        setPagination((prev) => ({
          ...prev,
          current: data.paging?.currentPage || 1,
          total: data.paging?.totalCount || 0,
        }));
      } else {
        message.error(response.errorMessage || t('personnel.queryFailed'));
      }
    } catch (error) {
      console.error('Query failed:', error);
      message.error(t('personnel.queryFailed'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleSearch = (values) => {
    form.setFieldsValue(values);
    setPagination((prev) => ({ ...prev, current: 1 }));
    fetchData({ currentPage: 1 });
  };

  const handleReset = () => {
    form.resetFields();
    setPagination((prev) => ({ ...prev, current: 1 }));
    fetchData({ currentPage: 1 });
  };

  const handleTableChange = (paginationConfig) => {
    const newPagination = {
      ...pagination,
      current: paginationConfig.current,
      pageSize: paginationConfig.pageSize,
    };
    setPagination(newPagination);
    fetchData({
      currentPage: paginationConfig.current,
      pageSize: paginationConfig.pageSize,
    });
  };

  const handleAddLevel = () => {
    setCurrentLevel(null);
    setRuleDrawerVisible(true);
  };

  const handleEditRules = (record) => {
    setCurrentLevel(record);
    setRuleDrawerVisible(true);
  };

  const handleBindPersonnel = (record) => {
    setCurrentLevel(record);
    setBindDrawerVisible(true);
  };

  const handleDelete = async (id) => {
    try {
      const response = await personnelService.deletePersonnelLevel(id);
      if (response.success) {
        message.success(t('personnel.deleteSuccess'));
        fetchData();
      } else {
        const errorMsg = response.errorCode === 'B2003'
          ? t('personnel.levelHasBoundError')
          : (response.errorMessage || t('personnel.deleteFailed'));
        message.error(errorMsg);
      }
    } catch (error) {
      console.error('Delete failed:', error);
      message.error(t('personnel.deleteFailed'));
    }
  };

  const handleRuleDrawerClose = () => {
    setRuleDrawerVisible(false);
    setCurrentLevel(null);
  };

  const handleRuleDrawerSuccess = () => {
    setRuleDrawerVisible(false);
    setCurrentLevel(null);
    fetchData();
  };

  const handleBindDrawerClose = () => {
    setBindDrawerVisible(false);
    setCurrentLevel(null);
  };

  const handleBindDrawerSuccess = () => {
    setBindDrawerVisible(false);
    setCurrentLevel(null);
    fetchData();
  };

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <FilterPanel
        form={form}
        onFilter={handleSearch}
        onReset={handleReset}
        initialValues={{}}>        
        <Col span={6}>
          <Form.Item name="levelName" label={t('personnel.levelName')}>
            <Input placeholder={t('personnel.levelNamePlaceholder')} />
          </Form.Item>
        </Col>
      </FilterPanel>

      <Card style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column', marginTop: 16 }} bodyStyle={{ flex: 1, overflow: 'hidden', padding: '0 24px', display: 'flex', flexDirection: 'column' }}>
        <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between' }}>
          <Button type="primary" icon={<PlusOutlined />} onClick={handleAddLevel}>
            {t('personnel.addLevel')}
          </Button>
        </div>
        <div ref={tableWrapRef} style={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
        <Table
          columns={columns}
          dataSource={dataSource}
          loading={loading}
          pagination={pagination}
          onChange={handleTableChange}
          rowKey="id"
          scroll={{ x: 1000, y: scrollY }}
        />
        </div>
      </Card>

      <LevelRuleDrawer
        visible={ruleDrawerVisible}
        level={currentLevel}
        onClose={handleRuleDrawerClose}
        onSuccess={handleRuleDrawerSuccess}
      />

      <BindPersonnelDrawer
        visible={bindDrawerVisible}
        level={currentLevel}
        onClose={handleBindDrawerClose}
        onSuccess={handleBindDrawerSuccess}
      />

    </div>
  );
};

export default PersonnelManagement;
